import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { EmployeePosition, EmployeePositionRequestBody } from './types';

export const employeePositionApi = createApi({
  reducerPath: 'employeePositionApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/employee-positions`,
  }),
  tagTypes: ['EmployeePosition', 'EmployeePositions'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getEmployeePositions: builder.query<EmployeePosition[], void>({
      query: () => ({
        url: '/',
      }),
      providesTags: () => ['EmployeePosition', 'EmployeePositions'],
    }),
    getEmployeePositionsI: builder.infiniteQuery<
      EmployeePosition[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['EmployeePosition', 'EmployeePositions'],
    }),
    getEmployeePositionById: builder.query<EmployeePosition, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['EmployeePosition'],
    }),
    createEmployeePosition: builder.mutation<
      EmployeePosition,
      EmployeePositionRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'EmployeePosition',
        { type: 'EmployeePosition', id: result?.id },
      ],
    }),
    updateEmployeePosition: builder.mutation<
      EmployeePosition,
      EmployeePositionRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'EmployeePosition',
        { type: 'EmployeePosition', id: result?.id },
      ],
    }),
    deleteEmployeePositionById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'EmployeePosition',
        { type: 'EmployeePosition', id },
      ],
    }),
  }),
});

export const {
  useGetEmployeePositionsQuery,
  useGetEmployeePositionByIdQuery,
  useCreateEmployeePositionMutation,
  useUpdateEmployeePositionMutation,
  useDeleteEmployeePositionByIdMutation,
} = employeePositionApi;

export const useGetEmployeePositionsIInfiniteQuery = normalizeInfiniteQueryData(
  employeePositionApi.useGetEmployeePositionsIInfiniteQuery,
);
